import { Component, OnInit, OnDestroy, EventEmitter, Output, Input, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription, forkJoin } from 'rxjs';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { TranslationService } from '../../services/translation.service';
import { ToastService } from '../../services/toast.service';
import { CodebookService } from '../codebook.service';
import { DefectService } from '../defect.service';
import { SiteService } from '../../site-selector/site.service';
import { DefectUserRoleService } from '../defect-user-role.service';
import {
  IDefect,
  Defect,
  IDefectModel,
  ISeverityLevel,
  IDefectState,
  ICategory,
  ICategory2,
  ICategory3,
  IEmployee,
  IImageVm,
  ReportDefectEventData,
  DefectLocationInfo,
} from '../defect.models';
import { UnscannedBuildingDialogComponent } from '../unscanned-building-dialog/unscanned-building-dialog.component';

// Import styles
import './defect-editor.component.scss';

/**
 * Defect editor component for creating and editing defects.
 * Migrated from web-app-legacy/js/app/Components/Defects/Edit/defectEditor.ts
 */
@Component({
  selector: 'app-defect-editor',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe, UnscannedBuildingDialogComponent],
  template: `
    <div class="screen-modal-container defect-editor">

      <!-- Unscanned building selection dialog - shown before the editor form -->
      <app-unscanned-building-dialog
        *ngIf="showBuildingDialog"
        [buildings]="unscannedBuildings"
        (buildingSelected)="onBuildingSelected($event)">
      </app-unscanned-building-dialog>

      <ng-container *ngIf="!showBuildingDialog">

      <div>
        <a class="screen-close" (click)="close()"></a>
      </div>

      <!-- Loading indicator -->
      <div class="busy-indicator" *ngIf="isBusy">
        <div class="bounce-spinner">
          <div class="bounce1"></div>
          <div class="bounce2"></div>
          <div class="bounce3"></div>
        </div>
      </div>

      <form #defectForm="ngForm" (ngSubmit)="saveDefect()" novalidate autocomplete="off">
        <input autocomplete="off" name="hidden" type="text" style="display:none;">

        <!-- Title (readonly, auto-generated) -->
        <label>{{ 'defect_form_title_lbl' | translate }}</label>
        <input
          readonly
          class="form-input defect-title"
          [(ngModel)]="defect.title"
          name="title"
          type="text" />

        <!-- Room (functional area) - prefilled when reported from a functional area detail, read-only -->
        <ng-container *ngIf="defect.functionalAreaTitle">
          <label>Místnost</label>
          <input
            readonly
            class="form-input"
            [value]="defect.functionalAreaTitle"
            type="text" />
        </ng-container>

        <!-- Description -->
        <textarea
          [required]="isDescriptionRequired"
          name="description"
          class="form-input"
          [(ngModel)]="defect.description"
          [placeholder]="'defect_form_description_placeholder' | translate">
        </textarea>
        <span
          *ngIf="isDescriptionRequired && defectForm.submitted && defectForm.controls['description']?.invalid"
          class="validation-message">
          {{ 'defect_form_validation_message_field' | translate }}
          {{ 'defect_form_description_lbl' | translate }}
          {{ 'defect_form_validation_message' | translate }}
        </span>

        <div class="middle-panel-container">
          <div class="middle-panel">
            <!-- Defect state (edit mode only) -->
            <ng-container *ngIf="isEditMode">
              <label>{{ 'defect_form_state_lbl' | translate }}</label>
              <select
                name="state"
                class="form-input"
                [(ngModel)]="selectedStateId">
                <option [ngValue]="null" disabled>{{ 'defect_form_state_lbl' | translate }}</option>
                <option *ngFor="let state of defectStates" [ngValue]="state.id">
                  {{ state.title }}
                </option>
              </select>
            </ng-container>

            <!-- Severity -->
            <label>{{ 'defect_form_severity_lbl' | translate }}</label>
            <select
              required
              name="severity"
              class="form-input"
              [(ngModel)]="selectedSeverityId"
              (ngModelChange)="onSeverityChange()">
              <option [ngValue]="null" disabled>{{ 'defect_form_severity_lbl' | translate }}</option>
              <option *ngFor="let severity of severityLevels" [ngValue]="severity.id">
                {{ severity.title }}
              </option>
            </select>
            <span
              *ngIf="defectForm.submitted && defectForm.controls['severity']?.invalid"
              class="validation-message">
              {{ 'defect_form_validation_message_field' | translate }}
              {{ 'defect_form_severity_lbl' | translate }}
              {{ 'defect_form_validation_message' | translate }}
            </span>

            <!-- Category 1 -->
            <label>{{ 'defect_form_category1_lbl' | translate }}</label>
            <select
              required
              name="category1"
              class="form-input"
              [(ngModel)]="selectedCategory1Id"
              (ngModelChange)="onCategory1Change()">
              <option [ngValue]="null" disabled>{{ 'defect_form_category1_lbl' | translate }}</option>
              <option *ngFor="let cat of filteredCategories1" [ngValue]="cat.id">
                {{ cat.title }}
              </option>
            </select>
            <span
              *ngIf="defectForm.submitted && defectForm.controls['category1']?.invalid"
              class="validation-message">
              {{ 'defect_form_validation_message_field' | translate }}
              {{ 'defect_form_category1_lbl' | translate }}
              {{ 'defect_form_validation_message' | translate }}
            </span>

            <!-- Category 2 -->
            <label>{{ 'defect_form_category2_lbl' | translate }}</label>
            <select
              required
              name="category2"
              class="form-input"
              [(ngModel)]="selectedCategory2Id"
              (ngModelChange)="onCategory2Change()"
              [disabled]="!selectedCategory1Id">
              <option [ngValue]="null" disabled>{{ 'defect_form_category2_lbl' | translate }}</option>
              <option *ngFor="let cat of filteredCategories2" [ngValue]="cat.id">
                {{ cat.title }}
              </option>
            </select>
            <span
              *ngIf="defectForm.submitted && defectForm.controls['category2']?.invalid"
              class="validation-message">
              {{ 'defect_form_validation_message_field' | translate }}
              {{ 'defect_form_category2_lbl' | translate }}
              {{ 'defect_form_validation_message' | translate }}
            </span>

            <!-- Category 3 -->
            <label>{{ 'defect_form_category3_lbl' | translate }}</label>
            <select
              required
              name="category3"
              class="form-input"
              [(ngModel)]="selectedCategory3Id"
              (ngModelChange)="onCategory3Change()"
              [disabled]="!selectedCategory2Id">
              <option [ngValue]="null" disabled>{{ 'defect_form_category3_lbl' | translate }}</option>
              <option *ngFor="let cat of filteredCategories3" [ngValue]="cat.id">
                {{ cat.title }}
              </option>
            </select>
            <span
              *ngIf="defectForm.submitted && defectForm.controls['category3']?.invalid"
              class="validation-message">
              {{ 'defect_form_validation_message_field' | translate }}
              {{ 'defect_form_category3_lbl' | translate }}
              {{ 'defect_form_validation_message' | translate }}
            </span>

            <!-- ŠKODA Energo toggle (only for severity 1) -->
            <div class="skoda-energo-container" *ngIf="selectedSeverityId === 1">
              <label class="skoda-energo-label">{{ 'defect_form_skoda_energo_label' | translate }}</label>
              <label class="switch">
                <input type="checkbox" [(ngModel)]="sendToSkodaEnergo" name="sendToSkodaEnergo" [disabled]="!isWorkingHour">
                <span class="slider round"></span>
              </label>
            </div>
          </div>

          <!-- Image upload panel -->
          <div class="middle-panel">
            <div *ngFor="let picture of pictureFiles; let i = index" class="picture-box-container">
              <label *ngIf="!picture.isUploaded">Fotografie {{ i + 1 }}</label>
              <div class="picture-box">
                <div class="picture-box-thumb">
                  <div *ngIf="picture.isUploaded" class="picture-box-thumb-container">
                    <div *ngIf="!picture.isLoaded" class="bounce-spinner">
                      <div class="bounce1"></div>
                      <div class="bounce2"></div>
                      <div class="bounce3"></div>
                    </div>
                    <img *ngIf="picture.src" [src]="picture.src" (load)="picture.isLoaded = true" />
                  </div>
                </div>
                <input
                  *ngIf="!picture.isUploaded"
                  class="form-input"
                  [value]="picture.file?.name || ''"
                  type="text"
                  disabled />
                <label *ngIf="!picture.isUploaded" class="image-add-button">
                  {{ 'defect_form_file_btn' | translate }}
                  <input
                    [id]="'defect-file-input-' + picture.id"
                    type="file"
                    accept="image/*"
                    (change)="onFileSelected($event, picture)"
                    style="display: none;" />
                </label>
                <a class="image-button" (click)="addPicture()"><div class="horizontal-plus"></div><div class="vertical-plus"></div></a>
                <a class="image-button" (click)="removePicture(picture.id)"><div class="horizontal-plus"></div></a>
              </div>
            </div>
          </div>
        </div>

        <!-- Contact info panel -->
        <div class="contact-panel">
          <!-- Reporter info -->
          <div class="contact-panel-item">
            <div class="contact-panel-item">
              <label>{{ 'defect_form_createdby_lbl' | translate }}</label>
              <input readonly class="form-input" [value]="defect.createdBy" type="text" />
            </div>
            <div class="contact-panel-item">
              <label>{{ 'defect_form_contactphone_lbl' | translate }}</label>
              <input readonly class="form-input" [value]="defect.contactPhone" type="text" />
            </div>
            <div class="contact-panel-item">
              <label>{{ 'defect_form_contactemail_lbl' | translate }}</label>
              <input readonly class="form-input" [value]="defect.contactEmail" type="email" />
            </div>
          </div>

          <!-- Manager info -->
          <div class="contact-panel-item">
            <div class="contact-panel-item">
              <label>{{ 'defect_form_manager_lbl' | translate }}</label>
              <input readonly class="form-input" [value]="managersNames" type="text" />
            </div>
            <div class="contact-panel-item">
              <label>{{ 'defect_form_contactphone_lbl' | translate }}</label>
              <input readonly class="form-input" [value]="managersPhones" type="text" />
            </div>
            <div class="contact-panel-item">
              <label>{{ 'defect_form_contactemail_lbl' | translate }}</label>
              <input readonly class="form-input" [value]="managersEmails" type="text" />
            </div>
          </div>

          <!-- Coordinator info -->
          <div class="contact-panel-item">
            <div class="contact-panel-item">
              <label>{{ 'defect_form_coordinator_lbl' | translate }}</label>
              <input readonly class="form-input" [value]="coordinatorName" type="text" />
            </div>
            <div class="contact-panel-item">
              <label>{{ 'defect_form_contactphone_lbl' | translate }}</label>
              <input readonly class="form-input" [value]="coordinator?.phone || ''" type="text" />
            </div>
            <div class="contact-panel-item">
              <label>{{ 'defect_form_contactemail_lbl' | translate }}</label>
              <input readonly class="form-input" [value]="coordinator?.email || ''" type="text" />
            </div>
          </div>
        </div>

        <!-- Footer buttons -->
        <div class="defect-editor-footer">
          <button class="link-btn" type="submit">
            {{ (isEditMode ? 'defect_form_update_btn' : 'defect_form_report_btn') | translate }}
          </button>
          <a class="link-btn" (click)="close()">{{ 'defect_form_close_btn' | translate }}</a>
        </div>
      </form>

      </ng-container>
    </div>
  `,
  styles: []
})
export class DefectEditorComponent implements OnInit, OnDestroy {
  @Input() eventData: ReportDefectEventData | null = null;
  @Input() defectToEdit: IDefect | null = null;
  @Output() closeEvent = new EventEmitter<void>();
  @Output() defectCreated = new EventEmitter<IDefect>();
  @Output() defectUpdated = new EventEmitter<IDefect>();

  private codebookService = inject(CodebookService);
  private defectService = inject(DefectService);
  private siteService = inject(SiteService);
  private userRoleService = inject(DefectUserRoleService);
  private translationService = inject(TranslationService);
  private toastService = inject(ToastService);
  private cdr = inject(ChangeDetectorRef);

  // Form state
  defect: IDefect = new Defect();
  isBusy = false;
  isEditMode = false;
  isDescriptionRequired = false;
  sendToSkodaEnergo = false;
  isWorkingHour = false;

  // Codebook data
  severityLevels: ISeverityLevel[] = [];
  defectStates: IDefectState[] = [];
  filteredCategories1: ICategory[] = [];
  filteredCategories2: ICategory2[] = [];
  filteredCategories3: ICategory3[] = [];

  // Selected values
  selectedSeverityId: number | null = null;
  selectedStateId: number | null = null;
  selectedCategory1Id: number | null = null;
  selectedCategory2Id: number | null = null;
  selectedCategory3Id: number | null = null;

  // Location info
  buildingName = '';
  locationInfo: DefectLocationInfo | null = null;
  showBuildingDialog = false;
  unscannedBuildings: DefectLocationInfo[] = [];

  // Manager/Coordinator info
  managers: IEmployee[] = [];
  coordinator: IEmployee | null = null;
  managersNames = '';
  managersPhones = '';
  managersEmails = '';

  // Pictures
  pictureFiles: IImageVm[] = [];

  private subscriptions: Subscription[] = [];

  get coordinatorName(): string {
    if (!this.coordinator) return '';
    return `${this.coordinator.titleBefore || ''} ${this.coordinator.name} ${this.coordinator.surname} ${this.coordinator.titleAfter || ''}`.trim();
  }

  ngOnInit(): void {
    this.checkWorkingHours();
    this.loadCodebooks();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  /**
   * Initialize the form with location data.
   */
  initializeWithLocation(eventData: ReportDefectEventData): void {
    this.eventData = eventData;
    this.prepareNewDefect();
  }

  private checkWorkingHours(): void {
    // Check if current time is within working hours (6:00 - 14:00 on working days)
    const now = new Date();
    const day = now.getDay();
    const hour = now.getHours();

    // Monday to Friday (1-5), 6:00 - 14:00
    this.isWorkingHour = day >= 1 && day <= 5 && hour >= 6 && hour < 14;
    this.sendToSkodaEnergo = !this.isWorkingHour;
  }

  private loadCodebooks(): void {
    this.isBusy = true;

    const site = this.siteService.selectedSite;
    const siteId = site?.id || 1;

    forkJoin([
      this.codebookService.loadAllCodebooks(),
      this.codebookService.loadBuildingManagers(siteId),
    ]).subscribe({
      next: () => {
        this.severityLevels = this.codebookService.getSeverityLevels();
        this.defectStates = this.codebookService.getDefectStates();
        this.isBusy = false;

        // Prepare the form: edit mode takes precedence over a new-defect event
        if (this.defectToEdit) {
          this.prepareEditDefect();
        } else if (this.eventData) {
          this.prepareNewDefect();
        }

        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('[DefectEditor] Error loading codebooks:', err);
        this.isBusy = false;
        this.cdr.detectChanges();
      },
    });
  }

  private prepareNewDefect(): void {
    if (!this.eventData) return;

    this.defect = new Defect();
    this.defect.id = 0;
    this.defect.defectStateId = 1; // Reported
    this.defect.createdAt = Date.now();

    // Reported from a functional area detail: the room is known up front
    if (this.eventData.functionalArea) {
      this.defect.functionalAreaId = this.eventData.functionalArea.id;
      this.defect.functionalAreaTitle = this.eventData.functionalArea.title;
    }

    // Set creator from the SKOMAP employee record, not from OIDC claims.
    // createdByPersonalNumber has to be the Employee.PersonalNumber, because that is
    // what canEditDefect() compares against. The OIDC preferred_username is the AD
    // login (e.g. DZCAU7B), a different identifier, so a defect created with it could
    // never be edited again by its own author.
    this.subscriptions.push(
      this.userRoleService.loadUserInfo().subscribe((userInfo) => {
        const employee = userInfo?.employee;
        if (!employee) {
          return;
        }

        this.defect.createdBy = employee.fullName;
        this.defect.createdByPersonalNumber = employee.personalNumber;
        this.defect.contactEmail = employee.email;
        this.defect.contactPhone = employee.phone;
        this.cdr.detectChanges();
      })
    );

    // Initialize pictures
    this.pictureFiles = [];
    this.addEmptyPicture();

    // Reported from a functional area: its FMS building is known. Locating it by
    // the POI coordinates is not reliable for functional area POIs and would fall
    // back to the unscanned building / outdoor dialog.
    const functionalArea = this.eventData.functionalArea;
    if (functionalArea?.buildingId) {
      this.applyBuildingLocation(this.getFunctionalAreaBuilding(functionalArea.buildingId, functionalArea.buildingName));
    } else {
      this.locateBuilding();
    }

    this.cdr.detectChanges();
  }

  /**
   * Fill the form from an existing defect (edit mode).
   * Mirrors the legacy DefectEditorController.activate() flow.
   */
  private prepareEditDefect(): void {
    const source = this.defectToEdit;
    if (!source || source.id <= 0) return;

    this.isEditMode = true;
    // Work on a copy so the overview/detail data stays untouched until saved
    this.defect = { ...source };

    // The title is "<building>-<cat1>-<cat2>-<cat3>"; the category change
    // handlers rebuild it from this prefix
    if (this.defect.title) {
      this.buildingName = `${this.defect.title.split('-')[0]}-`;
    }

    this.selectedSeverityId = this.defect.severityLevelId || null;
    this.selectedStateId = this.defect.defectStateId || null;

    // Restore the category cascade without the change handlers, which would
    // reset child selections and rewrite the title
    this.filteredCategories1 = this.codebookService.getCategories1();
    this.selectedCategory1Id = this.defect.category1Id || null;
    if (this.selectedCategory1Id) {
      this.filteredCategories2 = this.codebookService.getCategory2ByParentId(this.selectedCategory1Id);
    }
    this.selectedCategory2Id = this.defect.category2Id || null;
    if (this.selectedCategory2Id) {
      this.filteredCategories3 = this.codebookService.getCategory3ByParentId(this.selectedCategory2Id);
    }
    this.selectedCategory3Id = this.defect.category3Id || null;
    if (this.selectedCategory3Id) {
      const cat3 = this.codebookService.getCategory3ById(this.selectedCategory3Id);
      this.isDescriptionRequired = !!cat3?.isDescriptionRequered;
    }

    // Managers and coordinator of the defect's building
    const buildingManagers = this.codebookService.getManagersByBuildingId(this.defect.buildingId);
    this.managers = buildingManagers.map((m) => m.manager);
    this.coordinator = buildingManagers.length > 0 ? buildingManagers[0].coordinator : null;
    this.updateManagerInfo();

    this.sendToSkodaEnergo = this.defect.reportToHelpdeskAt > 0 || !this.isWorkingHour;

    // Load already uploaded images. The picture data is fetched through
    // HttpClient (with the Bearer token) and shown from a data: URL.
    this.pictureFiles = [];
    this.defectService.getDefectImages(this.defect.id).subscribe((list) => {
      list.items.forEach((img, i) => {
        const picture: IImageVm = {
          id: i + 2,
          file: null,
          guid: img.id,
          name: img.id,
          isUploaded: true,
          isLoaded: false,
          src: null,
        };
        this.pictureFiles.push(picture);
        this.defectService.getImageDataUrl(img.id).subscribe({
          next: (url) => {
            picture.src = url;
            this.cdr.detectChanges();
          },
          error: (err) => {
            console.error('[DefectEditor] Error loading picture:', img.id, err);
            // Hide the spinner; the thumbnail stays empty
            picture.isLoaded = true;
            this.cdr.detectChanges();
          },
        });
      });
      this.addEmptyPicture();
      this.cdr.detectChanges();
    });

    this.cdr.detectChanges();
  }

  private getFunctionalAreaBuilding(buildingId: number, buildingName?: string): DefectLocationInfo {
    const buildingManagers = this.codebookService.getManagersByBuildingId(buildingId);
    return {
      id: buildingId,
      name: buildingName || `Building ${buildingId}`,
      managers: buildingManagers.map((m) => m.manager),
      coordinator: buildingManagers.length > 0 ? buildingManagers[0].coordinator : null!,
      isOutdoorLocation: false,
    };
  }

  private locateBuilding(): void {
    if (!this.eventData) return;

    const site = this.siteService.selectedSite;
    if (!site) {
      this.showUnscannedBuildingDialog(site?.id || 1);
      return;
    }

    this.isBusy = true;

    this.defectService
      .locateBuildingByCoordinates(site.id, site.navvisSiteId, this.eventData.globalLocation)
      .subscribe({
        next: (locationInfo) => {
          if (locationInfo) {
            this.applyBuildingLocation(locationInfo);
          } else {
            this.showUnscannedBuildingDialog(site.id);
          }

          this.isBusy = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('[DefectEditor] Error locating building:', err);
          this.showUnscannedBuildingDialog(site.id);
          this.isBusy = false;
          this.cdr.detectChanges();
        },
      });
  }

  private showUnscannedBuildingDialog(siteId: number): void {
    this.isBusy = true;

    this.defectService.getUnscannedBuildings(siteId).subscribe({
      next: (buildings) => {
        // Map buildings to DefectLocationInfo
        const locations: DefectLocationInfo[] = buildings.map((b: any) => ({
          id: b.id,
          name: b.name || b.code || `Building ${b.id}`,
          description: b.description || '',
          managers: b.managers || [],
          coordinator: b.coordinator,
          isOutdoorLocation: false,
        }));

        // Prepend "Outdoor" option
        locations.unshift({
          id: -1,
          name: 'Outdoor',
          description: undefined,
          managers: [],
          coordinator: null!,
          isOutdoorLocation: true,
        });

        this.unscannedBuildings = locations;
        this.showBuildingDialog = true;
        this.isBusy = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('[DefectEditor] Error loading unscanned buildings:', err);
        this.isBusy = false;
        this.cdr.detectChanges();
      },
    });
  }

  onBuildingSelected(building: DefectLocationInfo | null): void {
    this.showBuildingDialog = false;

    if (!building) {
      // User cancelled — close the editor
      this.close();
      return;
    }

    this.applyBuildingLocation(building);
    this.cdr.detectChanges();
  }

  private applyBuildingLocation(locationInfo: DefectLocationInfo): void {
    this.locationInfo = locationInfo;
    this.buildingName = `${locationInfo.name}-`;
    this.defect.title = this.buildingName;
    this.defect.buildingId = locationInfo.id;

    this.managers = locationInfo.managers || [];
    this.coordinator = locationInfo.coordinator;
    this.updateManagerInfo();

    // Filter categories based on indoor/outdoor
    const allCategories1 = this.codebookService.getCategories1();
    this.filteredCategories1 = allCategories1.filter(
      (c) => c.isOutdoor === locationInfo.isOutdoorLocation
    );
  }

  private updateManagerInfo(): void {
    this.managersNames = this.managers.map((m) => m.fullName).join(', ');
    this.managersPhones = this.managers.map((m) => m.phone).filter(Boolean).join(', ');
    this.managersEmails = this.managers.map((m) => m.email).filter(Boolean).join(', ');
  }

  // Category cascade handling
  onSeverityChange(): void {
    // Severity change doesn't affect other fields
  }

  onCategory1Change(): void {
    this.selectedCategory2Id = null;
    this.selectedCategory3Id = null;
    this.filteredCategories2 = [];
    this.filteredCategories3 = [];

    if (this.selectedCategory1Id) {
      const cat1 = this.codebookService.getCategory1ById(this.selectedCategory1Id);
      if (cat1) {
        this.defect.title = `${this.buildingName}${cat1.title}`;
        this.filteredCategories2 = this.codebookService.getCategory2ByParentId(this.selectedCategory1Id);
      }
    }
  }

  onCategory2Change(): void {
    this.selectedCategory3Id = null;
    this.filteredCategories3 = [];

    if (this.selectedCategory2Id) {
      const cat1 = this.codebookService.getCategory1ById(this.selectedCategory1Id!);
      const cat2 = this.codebookService.getCategory2ById(this.selectedCategory2Id);
      if (cat1 && cat2) {
        this.defect.title = `${this.buildingName}${cat1.title}-${cat2.title}`;
        this.filteredCategories3 = this.codebookService.getCategory3ByParentId(this.selectedCategory2Id);
      }
    }
  }

  onCategory3Change(): void {
    if (this.selectedCategory3Id) {
      const cat1 = this.codebookService.getCategory1ById(this.selectedCategory1Id!);
      const cat2 = this.codebookService.getCategory2ById(this.selectedCategory2Id!);
      const cat3 = this.codebookService.getCategory3ById(this.selectedCategory3Id);

      if (cat1 && cat2 && cat3) {
        this.defect.title = `${this.buildingName}${cat1.title}-${cat2.title}-${cat3.title}`;
        this.isDescriptionRequired = cat3.isDescriptionRequered;
      }
    }
  }

  // Image handling
  addPicture(): void {
    this.addEmptyPicture();

    // Open the file dialog of the empty row so the "+" button adds a picture directly
    const empty = this.pictureFiles.find((p) => !p.isUploaded && !p.file);
    if (!empty) return;

    this.cdr.detectChanges();
    const input = document.getElementById(`defect-file-input-${empty.id}`) as HTMLInputElement | null;
    input?.click();
  }

  private addEmptyPicture(): void {
    if (this.pictureFiles.length >= 3) return;

    const hasEmpty = this.pictureFiles.some((p) => !p.isUploaded && !p.file);
    if (hasEmpty) return;

    const maxId = this.pictureFiles.length > 0
      ? Math.max(...this.pictureFiles.map((p) => p.id))
      : 0;

    this.pictureFiles.push({
      id: maxId + 1,
      file: null,
      guid: null,
      name: '',
      isUploaded: false,
      isLoaded: false,
      src: null,
    });
  }

  removePicture(id: number): void {
    const item = this.pictureFiles.find((p) => p.id === id);
    if (!item) {
      return;
    }

    // Already stored on the server (edit mode) — confirm and delete remotely
    if (item.isUploaded && item.guid) {
      const message = this.translationService.translate('remove_picture_modal_msg', 'Remove this picture?');
      if (!window.confirm(message)) {
        return;
      }

      this.isBusy = true;
      this.defectService.deleteDefectImage(item.guid).subscribe({
        next: () => {
          this.pictureFiles.splice(this.pictureFiles.indexOf(item), 1);
          this.addEmptyPicture();
          this.isBusy = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('[DefectEditor] Error deleting image:', err);
          this.isBusy = false;
          this.cdr.detectChanges();
        },
      });
      return;
    }

    const notUploaded = this.pictureFiles.filter((p) => !p.isUploaded);
    if (notUploaded.length === 1 && !item.src && !item.file) {
      return;
    }

    this.pictureFiles.splice(this.pictureFiles.indexOf(item), 1);
    this.addEmptyPicture();
  }

  onFileSelected(event: Event, picture: IImageVm): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      picture.file = input.files[0];
      picture.name = input.files[0].name;
      this.addEmptyPicture();
    }
  }

  // Form submission
  saveDefect(): void {
    // Validate form
    if (!this.selectedSeverityId || !this.selectedCategory1Id ||
        !this.selectedCategory2Id || !this.selectedCategory3Id) {
      return;
    }

    if (this.isDescriptionRequired && !this.defect.description?.trim()) {
      return;
    }

    this.isBusy = true;

    // Set selected values on defect
    this.defect.severityLevelId = this.selectedSeverityId;
    this.defect.category1Id = this.selectedCategory1Id;
    this.defect.category2Id = this.selectedCategory2Id;
    this.defect.category3Id = this.selectedCategory3Id;
    this.defect.reportToHelpdeskAt =
      this.selectedSeverityId === 1 && this.sendToSkodaEnergo ? Date.now() : 0;

    const site = this.siteService.selectedSite;
    this.defect.siteId = site?.id || 1;

    if (this.defect.id > 0) {
      this.updateExistingDefect();
      return;
    }

    // Create defect model
    const defectModel: IDefectModel = {
      defect: this.defect as any,
      poi: {
        Id: this.eventData?.poiId || 0,
        Importance: 0,
        Location: {
          lat: this.eventData?.globalLocation.y || 0,
          lon: this.eventData?.globalLocation.x || 0,
          z: this.eventData?.globalLocation.z || 0,
        },
      },
    };

    this.defectService.createDefect(defectModel).subscribe({
      next: (result) => {
        // Upload images if any
        const files = this.pictureFiles
          .filter((p) => p.file !== null && !p.isUploaded)
          .map((p) => p.file!);

        if (files.length > 0) {
          this.defectService.uploadImages(result.defect.id, files).subscribe({
            next: () => {
              this.onDefectSaved(result.defect);
            },
            error: (err) => {
              console.error('[DefectEditor] Error uploading images:', err);
              // The defect itself is saved; tell the user the pictures are not
              this.onDefectSaved(result.defect, true);
            },
          });
        } else {
          this.onDefectSaved(result.defect);
        }
      },
      error: (err) => {
        console.error('[DefectEditor] Error creating defect:', err);
        this.toastService.error(this.translationService.translate('defect_form_save_error_msg', 'Během nahlášení závady se vyskytla chyba!'));
        this.isBusy = false;
        this.cdr.detectChanges();
      },
    });
  }

  private onDefectSaved(defect: IDefect, picturesFailed = false): void {
    this.isBusy = false;
    if (picturesFailed) {
      this.toastService.error(this.picturesUploadErrorMessage());
    } else {
      this.toastService.success(this.translationService.translate('defect_form_save_success_msg', 'Závada byla úspěšně nahlášena'));
    }
    this.defectCreated.emit(defect);
    this.close();
  }

  private picturesUploadErrorMessage(): string {
    return this.translationService.translate('defect_form_pictures_error_msg', 'Závada byla uložena, ale obrázky se nepodařilo nahrát.');
  }

  /**
   * Update flow (edit mode). The POI is kept as-is: it is loaded from the
   * backend and sent back unchanged, same as the legacy updateDefect().
   */
  private updateExistingDefect(): void {
    this.defect.lastUpdateAt = Date.now();
    if (this.selectedStateId) {
      this.defect.defectStateId = this.selectedStateId;
    }

    this.defectService.getDefectWithPoi(this.defect.id).subscribe({
      next: (detail) => {
        const defectModel: IDefectModel = {
          defect: this.defect as any,
          poi: detail.poi,
        };

        this.defectService.updateDefect(defectModel).subscribe({
          next: (result) => {
            const files = this.pictureFiles
              .filter((p) => p.file !== null && !p.isUploaded)
              .map((p) => p.file!);

            if (files.length > 0) {
              this.defectService.uploadImages(result.defect.id, files).subscribe({
                next: () => this.onDefectUpdated(result.defect),
                error: (err) => {
                  console.error('[DefectEditor] Error uploading images:', err);
                  // The defect itself is updated; tell the user the pictures are not
                  this.onDefectUpdated(result.defect, true);
                },
              });
            } else {
              this.onDefectUpdated(result.defect);
            }
          },
          error: (err) => this.onUpdateError(err),
        });
      },
      error: (err) => this.onUpdateError(err),
    });
  }

  private onDefectUpdated(defect: IDefect, picturesFailed = false): void {
    this.isBusy = false;
    if (picturesFailed) {
      this.toastService.error(this.picturesUploadErrorMessage());
    } else {
      this.toastService.success(this.translationService.translate('defect_form_update_success_msg', 'Závada byla úspěšna aktualizována!'));
    }
    this.defectUpdated.emit(defect);
    this.close();
  }

  private onUpdateError(err: any): void {
    console.error('[DefectEditor] Error updating defect:', err);
    this.toastService.error(this.translationService.translate('defect_form_update_error_msg', 'An error has occurred while updating the defect!'));
    this.isBusy = false;
    this.cdr.detectChanges();
  }

  close(): void {
    this.closeEvent.emit();
  }
}
